<template>
  <div>
    <div class="config-tab tab-content">
      <div class="content" v-if="cardsInTemplate">
        <div class="instructions">
          <h6>Bulk Delete</h6>
          <p class="body-1">
            Check each input you would like to clear the input for the
            {{ bulkUpdateCount }} selected client(s)
          </p>
        </div>
        <template v-for="card in cardsInTemplate">
          <h6 :key="`${card.id}-header`" class="card-title">{{ card.name }}</h6>
          <card
            bulkDelete
            :key="card.id"
            :id="card.id"
            :rows="card.fields"
            :category="computedCategory(card)"
          >
            <fields
              bulkDelete
              :rows="card.fields"
              :category="computedCategory(card)"
            />
          </card>
        </template>
      </div>
    </div>
    <bottomBar>
      <qvoButton text @click="exitBulkSettings">Cancel</qvoButton>
      <qvoButton :disabled="!hasChanges" @click="goToNextStep">Next</qvoButton>
    </bottomBar>
  </div>
</template>

<script>
/**
 * @fileoverview Config tab
 *
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */

import { mapGetters } from "vuex";
import cloneDeep from "lodash/cloneDeep";

import bottomBar from "@/components/bottom-bar";
import card from "@/components/bulkUpdate/card";
import qvoButton from "@/qux-common/qux22/components/molecules/button";

import fields from "./fields";

export default {
  name: "BulkDeleteConfigTab",
  components: { bottomBar, card, qvoButton, fields },
  props: {
    tab: {
      type: String,
    },
  },
  computed: {
    isLoadingTemplate() {
      return this.pendingCalls.includes("/client/template/retrieve");
    },
    configData() {
      return () => import(`@/components/client/configs/${this.tab}`);
    },
    cardsInTemplate() {
      const cards = cloneDeep(this.cards) || [];
      const templateConfigs = cloneDeep(this.templateConfigs) || {};

      const extractValidIdsAndTypes = () => {
        let idTypeMap = {};
        for (let category in templateConfigs) {
          for (let item of templateConfigs[category]) {
            idTypeMap[item.FieldId] = item.Type;
          }
        }
        return idTypeMap;
      };

      const validIdsAndTypes = extractValidIdsAndTypes();

      const filterFields = ({ fields, cardCategory } = {}) => {
        if (!fields) return;
        return fields.filter((field) => {
          const fieldToTest = field[0] || field;

          const category = fieldToTest.category || cardCategory;
          const categoryIds = templateConfigs[category].map(
            (item) => item.FieldId
          );

          // Check if the field exists in the valid list and its type isn't "content"
          if (
            categoryIds.includes(fieldToTest.id) &&
            validIdsAndTypes[fieldToTest.id] !== "content"
          ) {
            if (fieldToTest.layout) {
              for (let layoutGroup of fieldToTest.layout) {
                if (layoutGroup[0].items) {
                  layoutGroup[0].items = filterFields({
                    fields: layoutGroup[0].items,
                    cardCategory,
                  });
                }
              }
            }

            if (fieldToTest.answerFields) {
              fieldToTest.answerFields = fieldToTest.answerFields.filter(
                (id) => {
                  return (
                    Object.keys(validIdsAndTypes).includes(id) &&
                    validIdsAndTypes[id] !== "content"
                  );
                }
              );
            }

            return true;
          }

          return false;
        });
      };

      for (let card of cards) {
        const cardCategory = this.computedCategory(card);
        card.fields = filterFields({ fields: card.fields, cardCategory });
      }

      // Don't allow cards to show if they have no fields
      return cards.filter(card => card.fields.length);
    },
    fieldList() {
      const fieldData =
        this.configTemplate && this.configTemplate[this.category];
      return fieldData && fieldData.AvailableFieldList;
    },
    hasChanges() {
      if (!this.updatedConfigValues.bulkDelete) return false;
      return !!Object.keys(this.updatedConfigValues.bulkDelete).length;
    },
    ...mapGetters({
      mergedConfig: "getMergedConfig",
      configTemplate: "getConfigTemplate",
      pendingCalls: "getPendingCallList",
      bulkUpdateCount: "getBulkUpdateCount",
      templateConfigs: "getTemplateConfigs",
      updatedConfigValues: "getUpdatedConfigValues",
    }),
  },
  asyncComputed: {
    async cards() {
      const configData = await this.configData();
      return configData.cards;
    },
    async category() {
      const configData = await this.configData();
      return configData.category;
    },
  },
  methods: {
    computedCategory(currentCard) {
      if (currentCard.category) {
        return currentCard.category;
      } else {
        return this.category;
      }
    },
    goToNextStep() {
      this.$router.push("/clients/bulkDelete/summary");
    },
    exitBulkSettings() {
      this.$store.dispatch("resetBulkSettings");
      this.$router.push("/clients");
    },
  },
};
</script>

<style lang="scss" scoped>
.config-tab {
  display: flex;
}
.instructions {
  h6 {
    margin-bottom: $qvo-spacing-16;
  }
}
.content {
  width: 100%;
  margin: $qvo-spacing-24;
}
.card-title {
  margin-top: $qvo-spacing-48;
}
.card {
  margin-bottom: $qvo-spacing-24;
}
h6 {
  margin-bottom: $qvo-spacing-24;
}
nav {
  padding-top: 0;
  position: sticky;
  top: $qvo-spacing-24;
}
.bottom-bar {
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 1;
}
</style>